<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import CookieForm from './CookieForm';
import { useCookieStore } from '../store/cookie';
import { useMainStore } from '../store/index';
import { useCookies } from '@vueuse/integrations/useCookies';

const cookieStore = useCookieStore();
const store = useMainStore();

const openForm = ref(false);
const showCookie = ref(true);

const isNecessaryCookies = computed(() => cookieStore.cookieConsent?.necessary);

const cookieName = 'DigitalSales.CookieConsent';
const optionCookie = {
  path: '/',
  sameSite: 'Strict',
  secure: true,
  maxAge: 1296000,
};

const cookieUtil = useCookies([cookieName], { doNotParse: false });

const acceptAll = () => {
  const cookieValue = {
    necessary: true,
    performance: true,
    functional: true,
    targeting: true,
    socialMedia: true,
  };
  cookieUtil.set(cookieName, JSON.stringify(cookieValue), optionCookie);
  cookieStore.saveConsent(cookieValue);
  showCookie.value = false;
};

onMounted(() => {
  const dsCookie = cookieUtil.get(cookieName);
  if (dsCookie?.necessary) showCookie.value = false;
  cookieStore.saveConsent(dsCookie) || {};
});

watch(isNecessaryCookies, () => {
  if (isNecessaryCookies.value)
    showCookie.value = false;
});
</script>
<template>
  <div
    v-if="showCookie"
    class="cookie-footer"
  >
    <div class="cookie-description-container">
      <h4>{{ $i18n('cookiesFooterTittle') }}</h4>
      <p>
        {{ $i18n('cookiesFooterText1') }}
      </p>
      <p>{{ $i18n('cookiesFooterText2') }}</p>
      <p>
        {{ $i18n('cookiesFooterText3') }}
        <a
          class="text-white"
          :href="store.cookiesPrivacyLinkForActiveMarket"
          target="_blank"
          data-dd-action-name="cookies privacy link"
        >{{ $i18n('cookiesHere') }}
        </a>
      </p>
    </div>
    <div class="cookie-gutter" />
    <div class="button-container text-right">
      <button
        type="button"
        class="sdds-btn sdds-btn-primary sdds-btn-fullbleed"
        data-dd-action-name="cookies footer accept all"
        @click="acceptAll"
      >
        {{ $i18n('cookiesFooterBtnAcceptAll') }}
      </button>
      <button
        type="button"
        class="sdds-btn sdds-button-secondary-outlined sdds-btn-fullbleed"
        data-dd-action-name="cookies footer change"
        @click="openForm = true"
      >
        {{ $i18n('cookiesFooterBtnChange') }}
      </button>
    </div>
  </div>
  <CookieForm
    v-if="openForm"
    @close="openForm = false"
  />
</template>
<style scoped>
.text-white {
  color: white;
}

button {
  font-family: 'Scania Sans Semi Condensed', 'Scania Sans Condensed', Arial,
    Helvetica, sans-serif;
  background: none;
  -webkit-box-shadow: 0px 0px 0px transparent;
  box-shadow: 0px 0px 0px transparent;
  border: 0px solid transparent;
  cursor: pointer;
  outline: none;
  text-shadow: 0px 0px 0px transparent;
}

.sdds-btn {
  font-family: 'Scania Sans Semi Condensed', 'Scania Sans Condensed', Arial,
    Helvetica, sans-serif;
  font-size: 3.5rem;
  line-height: 4rem;
  letter-spacing: -0.01em;
  display: inline-block;
  border-radius: 1rem;
  border: none;
  -webkit-box-shadow: none;
  box-shadow: none;
  position: relative;
  text-decoration: none;
  cursor: pointer;
  height: 14rem;
  padding: var(--scope-spacing-element-20);
  border: 1px solid;
}

.sdds-btn.sdds-btn-primary {
  border: unset;
  background: var(--sdds-blue-500);
}

button:focus {
  outline: none;
}

.scope-modal-backdrop {
  z-index: 2999999999;
}

.cookie-footer {
  z-index: 1999999999;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  width: 100%;
  position: fixed;
  left: 0;
  padding: 40px;
  bottom: 0;
  background-color: var(--sdds-grey-958);
  color: #fff;
  font-family: 'Scania Sans', sans-serif;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-direction: row;
  flex-direction: row;
  -ms-flex-align: stretch;
  align-items: stretch;
  -ms-flex-wrap: wrap-reverse;
  flex-wrap: wrap-reverse;
  max-height: 80vh;
}

.cookie-footer h4 {
  font-size: 20px;
  line-height: 24px;
  letter-spacing: -0.01em;
  margin-top: 0;
}

.cookie-footer .text-right {
  text-align: right;
}

.cookie-footer>div {
  -ms-flex: 1;
  flex: 1;
}

.cookie-footer p {
  font-size: 14px;
  line-height: 20px;
  font-weight: 200;
  letter-spacing: -0.02em;
}

.cookie-footer .button-container {
  height: 100%;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-direction: column;
  flex-direction: column;
  -ms-flex-wrap: wrap-reverse;
  flex-wrap: wrap-reverse;
  max-width: 800px;
  gap: 16px;
}

.cookie-footer .cookie-description-container {
  max-width: 900px;
  flex: 2;
  max-height: 50vh;
  overflow-y: auto;
}

.sdds-btn .button-svg-only {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.sdds-button-secondary-outlined {
  border-color: #fff;
  color: #fff;
  background-color: transparent;
}

.cookie-icon {
  position: relative;
  left: -1px;
  bottom: 5px;
}

.cookie-button {
  position: static;
}

@media screen and (min-width: 769px) {
  .cookie-footer p {
    font-size: 16px;
    line-height: 24px;
  }
}

@media screen and (max-width: 768px) {
  .cookie-footer {
    -ms-flex-direction: column;
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 3rem;
  }

  .cookie-footer .cookie-description-container {
    max-width: unset;
  }

  .cookie-footer .button-container {
    -ms-flex-direction: column;
    flex-direction: column;
    max-width: unset;
  }
}
</style>
